/**
 * Reports — the rail's third Teach row.
 *
 * A door, not a screen of its own: every number here is one line of the course
 * report behind it, and the report is where a teacher acts on any of them. It
 * exists because "Reports" is in the design's rail and a rail row that opens a
 * course list one click away from what it promises is better than one that
 * opens nothing.
 */

import { teach } from '../api';
import { CourseMark } from '../components/Bits';
import { Empty, ErrorNote, Loading, PageTitle, Panel } from '../components/Chrome';
import { ds } from '../ds';
import { useRouter } from '../router';
import { HAIR, figure, meta, monoPlain, plural } from '../theme';
import { useLoad } from '../useLoad';

export default function Reports() {
  const { Button } = ds();
  const { navigate } = useRouter();
  const { data, error, loading } = useLoad(() => teach.courses(), 'Could not load your courses.');

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const rows = data.courses.filter((row) => row.card.state !== 'draft');

  return (
    <>
      <PageTitle title="Reports." meta={plural(rows.length, 'course')} />

      {rows.length === 0 ? (
        <Empty line="Publish a course to see how it is going.">
          <Button variant="secondary" size="sm" onClick={() => navigate('/teach/courses')}>
            Go to courses
          </Button>
        </Empty>
      ) : (
        <Panel>
          {rows.map((row) => (
            <div
              key={row.card.id}
              className="mv-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '13px 4px',
                borderTop: HAIR,
                flexWrap: 'wrap'
              }}
            >
              <CourseMark colour={row.card.colour} size={32} />
              <span style={{ flex: 1, minWidth: 160, fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                {row.card.title}
              </span>
              <span style={{ ...monoPlain, ...figure }}>
                {meta(
                  `${row.learners} learners`,
                  `${row.completed} done`,
                  row.pastDue > 0 ? `${row.pastDue} past deadline` : null
                )}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/teach/courses/${row.card.id}/report`)}
              >
                Open
              </Button>
            </div>
          ))}
        </Panel>
      )}
    </>
  );
}
